import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, Download, Loader2 } from 'lucide-react';
import api from '../lib/api';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

export const PdfViewerModal = ({ apiPath, filename, title, onClose }) => {
    const [blob, setBlob] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null);
    const [error, setError] = useState('');
    const [numPages, setNumPages] = useState(0);
    const [width, setWidth] = useState(800);
    const bodyRef = useRef(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        let url = null;
        api.get(apiPath, { responseType: 'blob', timeout: 0 })
            .then(res => { url = URL.createObjectURL(res.data); setBlob(res.data); setBlobUrl(url); })
            .catch(() => setError('Impossibile caricare il PDF'));
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [apiPath]);

    // Il tasto "indietro" del telefono chiude il visualizzatore invece di uscire dall'app
    useEffect(() => {
        if (!window.history.state?.pdfViewer) window.history.pushState({ pdfViewer: true }, '');
        const onPop = () => onCloseRef.current();
        const onKey = (e) => { if (e.key === 'Escape') window.history.back(); };
        window.addEventListener('popstate', onPop);
        window.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('popstate', onPop);
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, []);

    useEffect(() => {
        const measure = () => bodyRef.current && setWidth(Math.min(bodyRef.current.clientWidth - 24, 900));
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    const handleDownload = () => {
        if (!blob) return;
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div data-testid="pdf-viewer-modal" className="fixed inset-0 z-[60] bg-neutral-800 flex flex-col">
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#0A0A0A] border-b border-white/10" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
                <button type="button" data-testid="pdf-viewer-close-btn" onClick={() => window.history.back()}
                    className="inline-flex items-center gap-2 px-3 py-2 border border-white/20 text-white hover:border-[#00FF41] hover:text-[#00FF41] transition-colors text-sm">
                    <X className="w-4 h-4" /> Chiudi
                </button>
                <span className="text-white/70 text-sm truncate flex-1 text-center hidden sm:block">{title}</span>
                <button type="button" data-testid="pdf-viewer-download-btn" onClick={handleDownload} disabled={!blob}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#00FF41] text-black hover:bg-[#00CC33] disabled:opacity-50 transition-colors text-sm font-medium">
                    <Download className="w-4 h-4" /> Scarica
                </button>
            </div>
            <div ref={bodyRef} className="flex-1 overflow-auto py-4 flex flex-col items-center" data-testid="pdf-viewer-body">
                {error ? (
                    <p data-testid="pdf-viewer-error" className="text-red-400 mt-20">{error}</p>
                ) : !blobUrl ? (
                    <div className="mt-20 flex flex-col items-center gap-3 text-white/60 text-sm">
                        <Loader2 className="w-8 h-8 animate-spin text-[#00FF41]" /> Caricamento PDF…
                    </div>
                ) : (
                    <Document file={blobUrl} onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                        onLoadError={() => setError('Impossibile visualizzare il PDF, usa "Scarica"')}
                        loading={<Loader2 className="w-8 h-8 animate-spin text-[#00FF41] mt-20" />}>
                        {Array.from({ length: numPages }, (_, i) => (
                            <div key={i} className="mb-4 shadow-lg" data-testid={`pdf-viewer-page-${i + 1}`}>
                                <Page pageNumber={i + 1} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
                            </div>
                        ))}
                    </Document>
                )}
            </div>
        </div>
    );
};
